// src/web/client/src/pages/Onboarding.jsx - FIXED
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ArrowRight, 
  Store, 
  MessageSquare, 
  BarChart3, 
  Sparkles,
  AlertCircle,
  CheckCircle,
  Loader2,
  TestTube,
  ExternalLink
} from "lucide-react";
import DemoModeButton from '../components/DemoModeButton';
import { authenticatedFetch } from "@/utils/authenticatedFetch";
// Token persistence goes through shopifyAuth's saveSessionToken (URL id_token
// → persistIfFresher) — a raw localStorage write here could downgrade a
// fresher stored token to the stale entry token (the #103 ordering-bug family).
import { saveSessionToken } from "@/utils/shopifyAuth";

export default function Onboarding() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [shopDomain, setShopDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 Onboarding Debug:', {
      url: window.location.href,
      shop: urlParams.get('shop'),
      host: urlParams.get('host'),
      isInIframe: window.top !== window.self,
      localStorage_shop: localStorage.getItem('shop_domain'),
      localStorage_token: localStorage.getItem('shopify_session_token') ? 'EXISTS' : 'MISSING'
    });

    const alreadyRedirecting = sessionStorage.getItem('redirecting_to_dashboard');
    if (alreadyRedirecting) {
      sessionStorage.removeItem('redirecting_to_dashboard');
      setCheckingStatus(false);
      return;
    }

    const shopFromUrl = urlParams.get('shop');
    const hostFromUrl = urlParams.get('host');
    const isInIframe = window.top !== window.self;
    const embedded = isInIframe || hostFromUrl;
    
    setIsEmbedded(embedded);

    // If embedded with shop param from Shopify - user is already connected!
    if (embedded && shopFromUrl) {
      console.log('🛍️ Embedded mode detected with shop:', shopFromUrl);
      localStorage.setItem('shop_domain', shopFromUrl);
      localStorage.setItem('shopDomain', shopFromUrl);
      
      // Save the URL id_token if present — never downgrading a fresher
      // stored token (saveSessionToken reads the URL itself and skips when
      // the param is absent).
      saveSessionToken();
      
      // User is already connected through Shopify OAuth - mark as connected
      setIsConnected(true);
      setShopDomain(shopFromUrl.replace('.myshopify.com', ''));
      setCheckingStatus(false);
      return;
    }
    
    // If embedded without shop param but in iframe - go to dashboard
    if (embedded) {
      const existingShop = localStorage.getItem('shop_domain');
      if (existingShop && existingShop !== 'demo-shop.myshopify.com') {
        console.log('🛍️ Embedded mode, using existing shop:', existingShop);
        localStorage.setItem('onboarding_completed', 'true');
        sessionStorage.setItem('redirecting_to_dashboard', 'true');
        window.location.href = '/dashboard';
        return;
      }
    }
    
    // Check if already connected (standalone mode)
    const existingShop = localStorage.getItem('shop_domain');
    const token = localStorage.getItem('token') || localStorage.getItem('shopify_session_token');
    
    if (existingShop && existingShop !== 'undefined' && existingShop !== 'demo-shop.myshopify.com') {
      if (token && token !== 'undefined' && token !== 'demo-token') {
        setIsConnected(true);
        setShopDomain(existingShop.replace('.myshopify.com', ''));
      } else {
        setShopDomain(existingShop.replace('.myshopify.com', ''));
      }
    }
    
    setCheckingStatus(false);
  }, []);

  const handleShopifyConnect = () => {
    if (!shopDomain || shopDomain.trim() === '') {
      toast({
        title: "Enter your shop domain",
        description: "Please enter your Shopify store domain",
        variant: "destructive"
      });
      return;
    }

    let domain = shopDomain.trim().toLowerCase();
    if (!domain.includes('.myshopify.com')) {
      domain = domain.replace(/\s+/g, '-');
      domain = domain.replace(/[^a-z0-9-]/g, '');
      domain = `${domain}.myshopify.com`;
    }

    const shopRegex = /^[a-zA-Z0-9][a-zA-Z0-9\-]*\.myshopify\.com$/;
    if (!shopRegex.test(domain)) {
      toast({
        title: "Invalid shop domain",
        description: "Please enter a valid Shopify store domain",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    localStorage.clear();
    localStorage.setItem('shop_domain', domain);
    localStorage.setItem('shopDomain', domain);

    console.log('🔐 Starting OAuth for shop:', domain);
    const authUrl = `https://api.ecomoria.com/auth?shop=${domain}`;

    if (isEmbedded) {
      toast({
        title: "Opening Shopify authentication",
        description: "Please complete the authentication in the new window",
        duration: 10000
      });
      
      const authWindow = window.open(authUrl, '_blank', 'width=600,height=700');
      
      if (!authWindow) {
        toast({
          title: "Popup blocked",
          description: "Please allow popups for this site and try again",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      
      const checkInterval = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(checkInterval);
            setLoading(false);
            const token = localStorage.getItem('token');
            if (token && token !== 'demo-token') {
              navigate('/dashboard');
            } else {
              toast({
                title: "Authentication incomplete",
                description: "Please try connecting again",
                variant: "destructive"
              });
            }
          }
        } catch (e) {}
      }, 1000);
      
    } else {
      window.location.href = authUrl;
    }
  };

  const handleGoToDashboard = async () => {
    const shop = localStorage.getItem('shop_domain');
    try {
      await authenticatedFetch(`https://api.ecomoria.com/api/shops?shop_domain=${shop}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onboarding_completed: true })
      });
    } catch (e) {
      console.error('Failed to update onboarding status:', e);
    }
    localStorage.setItem('onboarding_completed', 'true');
    window.location.href = '/dashboard';
  };

  const features = [
    {
      icon: MessageSquare,
      title: "Automated Messages",
      description: "Send personalized thank you notes and review requests"
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track engagement and conversion metrics"
    },
    {
      icon: Sparkles,
      title: "AI-Powered Content",
      description: "Generate unique messages for each customer"
    }
  ];

  // Show loading while checking status
  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-600" />
          <p className="text-gray-600">Checking your store status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to Ecomoria
          </h1>
          <p className="text-xl text-gray-600">
            Boost customer loyalty with AI-powered post-purchase engagement
          </p>
          {isEmbedded && !isConnected && (
            <p className="text-sm text-gray-500 mt-2">
              Running in Shopify Admin - Authentication will open in a new window
            </p>
          )}
        </div>

        {/* Main Content - Show different UI based on connection status */}
        {isConnected ? (
          /* Connected State - Simple card to go to dashboard */
          <Card className="max-w-lg mx-auto border-2 border-green-200">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Your Store is Connected!</CardTitle>
              <CardDescription>
                <strong>{shopDomain}.myshopify.com</strong> is ready to go
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">{feature.title}</p>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={handleGoToDashboard}
                className="w-full bg-green-600 hover:bg-green-700" 
                size="lg"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Start Using Ecomoria
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Not Connected State - Show connect options */
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Connect Store Card */}
             {/* HIDDEN for Shopify Review - Install only via Shopify App Store
          
            <Card className="border-2 border-indigo-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Store className="w-6 h-6 text-indigo-600" />
                  Connect Your Shopify Store
                </CardTitle>
                <CardDescription>
                  Start sending personalized messages to your customers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Make sure you have admin access to your Shopify store
                    {isEmbedded && " • Authentication will open in a new window"}
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Your Shopify Store</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="your-store-name"
                      value={shopDomain}
                      onChange={(e) => setShopDomain(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleShopifyConnect()}
                      disabled={loading}
                    />
                    <span className="flex items-center text-gray-500">.myshopify.com</span>
                  </div>
                </div>
                <Button 
                  onClick={handleShopifyConnect} 
                  className="w-full" 
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Connect to Shopify
                      {isEmbedded && <ExternalLink className="w-4 h-4 ml-2" />}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            */}

            {/* Demo Mode Card 
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <TestTube className="w-6 h-6 text-purple-600" />
                  Try Demo Mode
                </CardTitle>
                <CardDescription>
                  Explore Ecomoria with sample data - no setup required
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="font-medium">{feature.title}</p>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <DemoModeButton className="w-full" size="lg" />
                
                <p className="text-xs text-center text-gray-500">
                  Perfect for testing before connecting your store
                </p>
              </CardContent>
            </Card>
            */}
          </div>
        )}

        {/* Features Grid - Only show when not connected */}
        {!isConnected && (
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="text-center">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}